import { useState, useCallback, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getDbTables, getDbTableData, getDbTableRowCount, executeDbQuery } from '@/lib/api'
import type { DbTable, DbTableData, DbQueryResult } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Database,
  Table2,
  Play,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Loader2,
  Search,
  Terminal,
} from 'lucide-react'

// ---------- Table Browser ----------

function TableList({
  tables,
  rowCounts,
  selected,
  onSelect,
}: {
  tables: DbTable[]
  rowCounts: Record<string, number | null>
  selected: string | null
  onSelect: (name: string) => void
}) {
  const [filter, setFilter] = useState('')
  const filtered = tables.filter((t) =>
    t.name.toLowerCase().includes(filter.toLowerCase())
  )

  return (
    <div className="flex flex-col h-full">
      <div className="px-3 py-2 border-b">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
          <input
            type="text"
            placeholder="Filter tables…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full rounded-md border bg-background px-8 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto py-1">
        {filtered.map((t) => {
          const rowCount = rowCounts[t.name] ?? (t.row_count >= 0 ? t.row_count : undefined)
          const countFailed = rowCounts[t.name] === null

          return (
            <button
              key={t.name}
              onClick={() => onSelect(t.name)}
              className={`flex w-full items-center justify-between px-3 py-2 text-sm transition-colors hover:bg-accent/60 ${
                selected === t.name ? 'bg-accent text-accent-foreground' : 'text-foreground'
              }`}
            >
              <span className="truncate font-mono text-xs">{t.name}</span>
              <Badge
                variant="secondary"
                className="ml-2 shrink-0 min-w-7 justify-center text-[10px] tabular-nums"
                title={countFailed ? 'Failed to load row count' : undefined}
              >
                {rowCount !== undefined ? (
                  rowCount.toLocaleString()
                ) : countFailed ? (
                  '—'
                ) : (
                  <Loader2 className="h-3 w-3 animate-spin" aria-label="Loading row count" />
                )}
              </Badge>
            </button>
          )
        })}
        {filtered.length === 0 && (
          <p className="px-3 py-4 text-xs text-muted-foreground text-center">No tables found</p>
        )}
      </div>
    </div>
  )
}

function TableDataView({
  tableName,
  onRowCount,
}: {
  tableName: string
  onRowCount: (tableName: string, rowCount: number) => void
}) {
  const [page, setPage] = useState(1)
  const pageSize = 100

  const { data, isFetching, error } = useQuery({
    queryKey: ['db-table-data', tableName, page, pageSize],
    queryFn: () => getDbTableData(tableName, page, pageSize),
    placeholderData: (prev) => prev,
  })

  const totalRows = data?.total_rows
  useEffect(() => {
    if (totalRows !== undefined) {
      onRowCount(tableName, totalRows)
    }
  }, [onRowCount, tableName, totalRows])

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="py-4 text-sm text-destructive">
          Failed to load table: {(error as Error).message}
        </CardContent>
      </Card>
    )
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const totalPages = Math.ceil(data.total_rows / pageSize) || 1

  return (
    <div className="space-y-3">
      {/* Schema badges */}
      <div className="flex flex-wrap gap-1.5">
        {data.columns.map((col, i) => (
          <Badge key={col} variant="outline" className="font-mono text-[11px] gap-1">
            {col}
            <span className="text-muted-foreground font-normal">{data.types[i] || ''}</span>
          </Badge>
        ))}
      </div>

      {/* Data table */}
      <div className="rounded-md border overflow-auto max-h-[65vh]">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-muted/80 backdrop-blur">
            <tr>
              {data.columns.map((col) => (
                <th
                  key={col}
                  className="whitespace-nowrap px-3 py-2 text-left font-semibold text-muted-foreground uppercase tracking-wider border-b"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="font-mono">
            {data.rows.map((row, ri) => (
              <tr key={ri} className="border-b last:border-0 hover:bg-accent/30 transition-colors">
                {(row as unknown[]).map((cell, ci) => (
                  <td key={ci} className="px-3 py-1.5 whitespace-nowrap max-w-[300px] truncate">
                    {cell === null ? (
                      <span className="text-muted-foreground italic">NULL</span>
                    ) : typeof cell === 'boolean' ? (
                      <Badge variant={cell ? 'default' : 'secondary'} className="text-[10px]">
                        {String(cell)}
                      </Badge>
                    ) : (
                      String(cell)
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {data.total_rows.toLocaleString()} row{data.total_rows !== 1 ? 's' : ''} total
          {isFetching && <Loader2 className="inline ml-2 h-3 w-3 animate-spin" />}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="h-7 px-2"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <span className="tabular-nums">
            {page} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="h-7 px-2"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  )
}

// ---------- Query Runner ----------

function QueryRunner() {
  const [sql, setSql] = useState('')
  const [result, setResult] = useState<DbQueryResult | null>(null)
  const [queryError, setQueryError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: (query: string) => executeDbQuery(query),
    onSuccess: (data) => {
      if (data.error) {
        setQueryError(data.error)
        setResult(null)
      } else {
        setResult(data)
        setQueryError(null)
      }
    },
    onError: (err) => {
      setQueryError((err as Error).message)
      setResult(null)
    },
  })

  const handleRun = useCallback(() => {
    if (!sql.trim()) return
    mutation.mutate(sql)
  }, [sql, mutation])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault()
        handleRun()
      }
    },
    [handleRun]
  )

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-4 space-y-3">
          <textarea
            value={sql}
            onChange={(e) => setSql(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="SELECT * FROM symbol_mapping LIMIT 10"
            rows={5}
            className="w-full rounded-md border bg-background px-3 py-2 font-mono text-sm resize-y focus:outline-none focus:ring-1 focus:ring-ring"
          />
          <div className="flex items-center justify-between">
            <p className="text-[11px] text-muted-foreground">
              Press <kbd className="rounded border bg-muted px-1 py-0.5">⌘</kbd>+
              <kbd className="rounded border bg-muted px-1 py-0.5">Enter</kbd> to run · Read-only queries only
            </p>
            <Button onClick={handleRun} disabled={mutation.isPending || !sql.trim()} className="gap-2">
              {mutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Play className="h-4 w-4" />
              )}
              Run Query
            </Button>
          </div>
        </CardContent>
      </Card>

      {queryError && (
        <Card className="border-destructive">
          <CardContent className="py-3 text-sm text-destructive font-mono">{queryError}</CardContent>
        </Card>
      )}

      {result && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-3">
              Results
              <Badge variant="secondary" className="text-[10px] tabular-nums">
                {result.row_count} row{result.row_count !== 1 ? 's' : ''}
              </Badge>
              <span className="text-xs text-muted-foreground font-normal">
                {result.execution_time_ms}ms
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {result.columns.length === 0 ? (
              <p className="text-sm text-muted-foreground">Query returned no results.</p>
            ) : (
              <div className="rounded-md border overflow-auto max-h-[55vh]">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                    <tr>
                      {result.columns.map((col) => (
                        <th
                          key={col}
                          className="whitespace-nowrap px-3 py-2 text-left font-semibold text-muted-foreground uppercase tracking-wider border-b"
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    {result.rows.map((row, ri) => (
                      <tr key={ri} className="border-b last:border-0 hover:bg-accent/30 transition-colors">
                        {(row as unknown[]).map((cell, ci) => (
                          <td key={ci} className="px-3 py-1.5 whitespace-nowrap max-w-[400px] truncate">
                            {cell === null ? (
                              <span className="text-muted-foreground italic">NULL</span>
                            ) : (
                              String(cell)
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// ---------- Main Page ----------

export default function DbBrowser() {
  const { isAdmin } = useAuth()
  const queryClient = useQueryClient()
  const [selectedTable, setSelectedTable] = useState<string | null>(null)
  const [rowCounts, setRowCounts] = useState<Record<string, number | null>>({})

  const tablesQuery = useQuery({
    queryKey: ['db-tables'],
    queryFn: getDbTables,
    refetchInterval: 30000,
  })

  const tableNamesKey = (tablesQuery.data?.tables ?? []).map((table) => table.name).join('\0')

  useEffect(() => {
    if (!isAdmin || !tableNamesKey) return

    let cancelled = false
    const tableNames = tableNamesKey.split('\0')

    const loadRowCounts = async () => {
      for (const tableName of tableNames) {
        if (cancelled) return
        try {
          const result = await queryClient.fetchQuery({
            queryKey: ['db-table-row-count', tableName],
            queryFn: () => getDbTableRowCount(tableName),
            staleTime: 30000,
          })
          if (!cancelled) {
            setRowCounts((current) => ({ ...current, [tableName]: result.row_count }))
          }
        } catch {
          if (!cancelled) {
            setRowCounts((current) => ({ ...current, [tableName]: null }))
          }
        }
      }
    }

    void loadRowCounts()
    return () => {
      cancelled = true
    }
  }, [isAdmin, queryClient, tableNamesKey])

  const handleRowCount = useCallback((tableName: string, rowCount: number) => {
    setRowCounts((current) => ({ ...current, [tableName]: rowCount }))
    queryClient.setQueryData(
      ['db-table-row-count', tableName],
      { table: tableName, row_count: rowCount }
    )
  }, [queryClient])

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
        <AlertTriangle className="h-12 w-12 text-destructive" />
        <h2 className="text-xl font-semibold">Access Denied</h2>
        <p className="text-muted-foreground">This page is restricted to administrators.</p>
      </div>
    )
  }

  const tables = tablesQuery.data?.tables ?? []

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center gap-3">
        <Database className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">DB Browser</h1>
          <p className="text-sm text-muted-foreground">
            Browse tables and run read-only queries · {tables.length} table{tables.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      <Tabs defaultValue="tables" className="w-full">
        <TabsList>
          <TabsTrigger value="tables" className="gap-1.5">
            <Table2 className="h-3.5 w-3.5" /> Tables
          </TabsTrigger>
          <TabsTrigger value="query" className="gap-1.5">
            <Terminal className="h-3.5 w-3.5" /> Query
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tables">
          {tablesQuery.isError ? (
            <Card className="border-destructive">
              <CardContent className="py-4 text-sm text-destructive">
                Failed to load tables: {(tablesQuery.error as Error).message}
              </CardContent>
            </Card>
          ) : !tablesQuery.data ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="grid grid-cols-[240px_1fr] gap-4 min-h-[60vh]">
              <Card className="overflow-hidden">
                <TableList
                  tables={tables}
                  rowCounts={rowCounts}
                  selected={selectedTable}
                  onSelect={setSelectedTable}
                />
              </Card>
              <div>
                {selectedTable ? (
                  <TableDataView tableName={selectedTable} onRowCount={handleRowCount} />
                ) : (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Table2 className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                      <p className="text-sm text-muted-foreground">
                        Select a table from the list to view its data.
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="query">
          <QueryRunner />
        </TabsContent>
      </Tabs>
    </div>
  )
}
